import {
  createPanelFrame,
  resolveTitle,
  createMetric,
  formatNumber,
  toNumber
} from './panel-utils.js';

const DEFAULT_COMMITS = Array.from({ length: 60 }, (_, i) => ({
  id: `c${60 - i}`,
  hash: `a${(Math.random() * 1e8).toString(16).slice(0, 7)}`,
  message: i % 6 === 0 ? 'Refactor pipeline scheduler' : i % 5 === 0 ? 'Fix model registry sync' : 'Update feature store contract',
  author: i % 3 === 0 ? 'A. Vega' : 'S. Rao',
  timestamp: Date.now() - i * 3600 * 1000,
  branch: i % 4 === 0 ? 'main' : 'release/v4',
  tags: i % 10 === 0 ? ['v4.2.1'] : [],
  stats: { files: 4 + (i % 3), additions: 24 + i, deletions: 8 + (i % 6) },
  files: Array.from({ length: 4 + (i % 3) }, (_, f) => ({
    name: `src/module_${(f + i) % 9}.js`,
    additions: 3 + (f * 2),
    deletions: 1 + (f % 2),
    status: f % 2 === 0 ? 'M' : 'A'
  }))
}));

export function render({ node, api }) {
  const { root, body, titleEl, metaEl } = createPanelFrame(node, 'Git Log History');
  let currentNode = node;

  const toolbar = document.createElement('div');
  toolbar.className = 'ew-git-toolbar';
  const search = document.createElement('input');
  search.type = 'search';
  search.placeholder = 'Search message';

  const authorSelect = document.createElement('select');
  const branchSelect = document.createElement('select');
  const clearBtn = document.createElement('button');
  clearBtn.type = 'button';
  clearBtn.className = 'ew-btn-mini';
  clearBtn.textContent = 'Clear';

  toolbar.append(search, authorSelect, branchSelect, clearBtn);

  const metrics = document.createElement('div');
  metrics.className = 'ew-metric-grid';

  const compareBar = document.createElement('div');
  compareBar.className = 'ew-compare-bar';

  const list = document.createElement('div');
  list.className = 'ew-git-list';

  const footer = document.createElement('div');
  footer.className = 'ew-git-footer';
  const loadMore = document.createElement('button');
  loadMore.type = 'button';
  loadMore.className = 'ew-btn-mini';
  loadMore.textContent = 'Load more';
  footer.appendChild(loadMore);

  body.append(toolbar, metrics, compareBar, list, footer);

  const state = { pageSize: 30, visibleCount: 30 };

  const update = (next) => {
    currentNode = next;
    titleEl.textContent = resolveTitle(next, 'Git Log History');
    const rawCommits = Array.isArray(next?.data?.commits || next?.props?.commits)
      ? (next.data?.commits || next.props?.commits)
      : DEFAULT_COMMITS;

    const pageSize = Math.max(10, toNumber(next?.props?.pageSize ?? next?.data?.pageSize, state.pageSize));
    state.pageSize = pageSize;
    const storedVisible = toNumber(next?.data?.visibleCount, NaN);
    state.visibleCount = Number.isFinite(storedVisible) ? storedVisible : pageSize;

    const normalized = normalizeCommits(rawCommits);
    const filters = next?.data?.filters || {};
    const query = (filters.query ?? search.value ?? '').toLowerCase();
    const author = filters.author ?? authorSelect.value;
    const branch = filters.branch ?? branchSelect.value;

    metaEl.textContent = `${normalized.length} commits`;

    populateSelect(authorSelect, 'Author', normalized.map((c) => c.author));
    populateSelect(branchSelect, 'Branch', normalized.map((c) => c.branch));
    if (author) authorSelect.value = author;
    if (branch) branchSelect.value = branch;
    search.value = filters.query ?? search.value ?? '';

    const filtered = normalized.filter((c) => {
      if (author && c.author !== author) return false;
      if (branch && c.branch !== branch) return false;
      if (query && !c.message.toLowerCase().includes(query)) return false;
      return true;
    });

    const compare = next?.data?.compare || {};
    const selected = next?.data?.selected || null;

    metrics.innerHTML = '';
    const additions = filtered.reduce((acc, c) => acc + toNumber(c.stats?.additions, 0), 0);
    const deletions = filtered.reduce((acc, c) => acc + toNumber(c.stats?.deletions, 0), 0);
    metrics.append(
      createMetric('visible', `${Math.min(state.visibleCount, filtered.length)}/${filtered.length}`),
      createMetric('adds', formatNumber(additions)),
      createMetric('deletes', formatNumber(deletions))
    );

    renderCompare(compareBar, compare, normalized, api, node.id);

    list.innerHTML = '';
    const visible = filtered.slice(0, state.visibleCount);
    visible.forEach((commit) => {
      const item = document.createElement('div');
      item.className = 'ew-git-commit';
      if (commit.id === selected) item.classList.add('is-selected');
      if (compare.a === commit.id) item.classList.add('is-compare-a');
      if (compare.b === commit.id) item.classList.add('is-compare-b');

      const header = document.createElement('button');
      header.type = 'button';
      header.className = 'ew-git-header';
      header.addEventListener('click', () => {
        api.setData({ selected: commit.id });
        api.emit('node:input', { id: node.id, field: 'commit', value: commit.id });
      });

      const hash = document.createElement('span');
      hash.className = 'ew-git-hash';
      hash.textContent = commit.hash;
      const message = document.createElement('span');
      message.className = 'ew-git-message';
      message.textContent = commit.message;
      header.append(hash, message);

      const meta = document.createElement('div');
      meta.className = 'ew-git-meta';
      meta.textContent = `${commit.author} | ${formatDate(commit.timestamp)} | ${commit.branch}`;

      const actions = document.createElement('div');
      actions.className = 'ew-git-actions';
      const btnA = document.createElement('button');
      btnA.type = 'button';
      btnA.className = 'ew-btn-mini';
      btnA.textContent = 'A';
      btnA.addEventListener('click', (e) => {
        e.stopPropagation();
        const nextCompare = { a: commit.id, b: compare.b === commit.id ? null : compare.b };
        if (nextCompare.b === commit.id) nextCompare.b = null;
        api.setData({ compare: nextCompare });
        api.emit('node:input', { id: node.id, field: 'compare', value: nextCompare });
      });
      const btnB = document.createElement('button');
      btnB.type = 'button';
      btnB.className = 'ew-btn-mini';
      btnB.textContent = 'B';
      btnB.addEventListener('click', (e) => {
        e.stopPropagation();
        const nextCompare = { a: compare.a, b: commit.id };
        if (nextCompare.a === commit.id) nextCompare.a = null;
        api.setData({ compare: nextCompare });
        api.emit('node:input', { id: node.id, field: 'compare', value: nextCompare });
      });
      actions.append(btnA, btnB);

      const diffMeta = document.createElement('div');
      diffMeta.className = 'ew-git-diff-meta';
      const files = formatNumber(toNumber(commit.stats?.files, commit.files.length));
      const adds = formatNumber(toNumber(commit.stats?.additions, 0));
      const dels = formatNumber(toNumber(commit.stats?.deletions, 0));
      diffMeta.textContent = `${files} files | +${adds} -${dels}`;

      const fileList = document.createElement('div');
      fileList.className = 'ew-git-files';
      const maxFiles = toNumber(next?.props?.maxFiles ?? next?.data?.maxFiles, 4);
      const filesSlice = commit.files.slice(0, maxFiles);
      filesSlice.forEach((file) => {
        const row = document.createElement('div');
        row.className = 'ew-git-file';
        const name = document.createElement('span');
        name.className = 'ew-git-file-name';
        name.textContent = file.name;
        const chips = document.createElement('span');
        chips.className = 'ew-git-file-chips';
        const status = document.createElement('span');
        status.className = 'ew-diff-chip';
        status.textContent = file.status || 'M';
        const plus = document.createElement('span');
        plus.className = 'ew-diff-chip ew-diff-plus';
        plus.textContent = `+${formatNumber(toNumber(file.additions, 0))}`;
        const minus = document.createElement('span');
        minus.className = 'ew-diff-chip ew-diff-minus';
        minus.textContent = `-${formatNumber(toNumber(file.deletions, 0))}`;
        chips.append(status, plus, minus);
        row.append(name, chips);
        fileList.appendChild(row);
      });
      if (commit.files.length > maxFiles) {
        const more = document.createElement('div');
        more.className = 'ew-git-file-more';
        more.textContent = `+${commit.files.length - maxFiles} more files`;
        fileList.appendChild(more);
      }

      item.append(header, meta, diffMeta, fileList, actions);
      list.appendChild(item);
    });

    loadMore.style.display = state.visibleCount < filtered.length ? 'inline-flex' : 'none';
  };

  search.addEventListener('input', () => {
    const filters = currentNode?.data?.filters || {};
    api.setData({ filters: { ...filters, query: search.value } });
  });
  authorSelect.addEventListener('change', () => {
    const filters = currentNode?.data?.filters || {};
    api.setData({ filters: { ...filters, author: authorSelect.value } });
  });
  branchSelect.addEventListener('change', () => {
    const filters = currentNode?.data?.filters || {};
    api.setData({ filters: { ...filters, branch: branchSelect.value } });
  });
  clearBtn.addEventListener('click', () => {
    api.setData({ filters: { query: '', author: '', branch: '' } });
  });
  loadMore.addEventListener('click', () => {
    const nextCount = state.visibleCount + state.pageSize;
    api.setData({ visibleCount: nextCount });
  });

  update(node);
  return { el: root, update };
}

function normalizeCommits(commits) {
  return commits
    .map((commit, idx) => ({
      id: commit.id || commit.hash || `commit-${idx}`,
      hash: commit.hash ? String(commit.hash).slice(0, 7) : `c${idx}`,
      message: commit.message || 'Commit update',
      author: commit.author || 'Unknown',
      timestamp: commit.timestamp || Date.now() - idx * 3600 * 1000,
      branch: commit.branch || 'main',
      tags: Array.isArray(commit.tags) ? commit.tags : [],
      stats: commit.stats || { files: commit.files?.length || 0, additions: 0, deletions: 0 },
      files: Array.isArray(commit.files) ? commit.files : []
    }))
    .sort((a, b) => b.timestamp - a.timestamp);
}

function populateSelect(select, label, values) {
  const unique = Array.from(new Set(values.filter(Boolean)));
  select.innerHTML = '';
  const placeholder = document.createElement('option');
  placeholder.value = '';
  placeholder.textContent = label;
  select.appendChild(placeholder);
  unique.forEach((value) => {
    const option = document.createElement('option');
    option.value = value;
    option.textContent = value;
    select.appendChild(option);
  });
}

function formatDate(ts) {
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function renderCompare(container, compare, commits, api, nodeId) {
  container.innerHTML = '';
  const label = document.createElement('span');
  label.className = 'ew-compare-label';
  label.textContent = 'Compare:';
  const a = document.createElement('span');
  a.className = 'ew-compare-pill';
  a.textContent = commitLabel(compare.a, commits) || 'A';
  const b = document.createElement('span');
  b.className = 'ew-compare-pill';
  b.textContent = commitLabel(compare.b, commits) || 'B';
  const clear = document.createElement('button');
  clear.type = 'button';
  clear.className = 'ew-btn-mini';
  clear.textContent = 'Clear';
  clear.addEventListener('click', () => {
    api.setData({ compare: { a: null, b: null } });
    api.emit('node:input', { id: nodeId, field: 'compare', value: { a: null, b: null } });
  });
  container.append(label, a, b, clear);
}

function commitLabel(id, commits) {
  if (!id) return '';
  const found = commits.find((c) => c.id === id);
  if (!found) return id;
  return `${found.hash} ${found.author}`;
}

